import React, {Fragment} from 'react';

import cs from 'classnames';
import jquery from 'jquery';
import {colors} from 'js-libs/sweetcommons/styles';
import {Link} from 'react-router';
import styled from 'styled-components';

import {CopyLinkButton} from 'app/components/general/CopyLinkButton';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {DocIsDraftBadge} from 'app/general/dashboards/DocIsDraftBadge';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {OrphanStepBadge} from 'app/procedures/components/OrphanStepBadge';
import {ProcedureStepFiles} from 'app/procedures/components/ProcedureStepFiles';
import {StyledStepContentSeparatorDiv} from 'app/procedures/components/structures';
import {COLLAPSE_DURATION_MS} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {ProcedureStepStructure} from '../ProcedureStepStructure';

import {ProcedureAutomationStepType} from 'sweet-types/procedure/procedure-step';

import type {ProcedureStepWithDiffType} from 'app/tasks/types';
import type {
  AnyStepType,
  DeletedProcessProcedureAsStepSerializedType,
  ProcedureContentStepType,
  ProcedureDecisionStepType,
  ProcedureEndingStepType,
  ProcessProcedureAsStepSerializedType,
  RegularProcessProcedureAsStepInTaskSerializedType,
} from 'sweet-types';

export const StyledCollapseButton = styled.button`
  line-height: 1;
  height: 2rem;
  color: ${colors.lightGray};
  cursor: pointer;
  &:hover {
    color: ${colors.mediumGray};
  }
`;
const StyledTitleDiv = styled.div`
  .copy-link {
    display: none;
    font-size: 0.5em;
    color: ${colors.light};
  }

  &:hover {
    .copy-link {
      display: inline-block;
    }
  }
`;
type Props = {
  index: number | string;
  step:
    | ProcedureStepWithDiffType
    | AnyStepType
    | DeletedProcessProcedureAsStepSerializedType
    | ProcessProcedureAsStepSerializedType
    | RegularProcessProcedureAsStepInTaskSerializedType;
  collapsed: boolean;
  onToggleCollapse: (id: string, collapse: boolean) => any;
  mediaURL?: string;
  warnIncomplete: boolean;
  orphan?: boolean;
  mediaURLRead?: string;
  actionButtons?: JSX.Element | null;
  fieldInputs?: JSX.Element | null;
  choicesList?: JSX.Element | null;
  automation?: JSX.Element | null;
  next?: JSX.Element;
  previous?: JSX.Element;
  showCopyLink?: boolean;
};
export class ProcedureStep extends React.PureComponent<Props> {
  static defaultProps = {
    warnIncomplete: false,
  };
  collapsableEl: JSX.Element;

  componentDidMount() {
    const {collapsed} = this.props;

    if (collapsed) {
      this.toggleCollapse(true, true);
    }
  }

  toggleCollapse = (collapse: boolean, disableAnimation = false) => {
    const command = collapse ? 'slideUp' : 'slideDown';
    jquery(this.collapsableEl)[command](
      disableAnimation ? 0 : COLLAPSE_DURATION_MS
    );
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.collapsed !== this.props.collapsed) {
      this.toggleCollapse(nextProps.collapsed);
    }
  }

  wrapInLinkTag(
    content: JSX.Element,
    step:
      | ProcedureStepWithDiffType
      | ProcedureContentStepType
      | ProcedureDecisionStepType
      | ProcedureAutomationStepType
      | ProcedureEndingStepType
      | DeletedProcessProcedureAsStepSerializedType
      | AnyStepType
      | ProcessProcedureAsStepSerializedType
      | RegularProcessProcedureAsStepInTaskSerializedType
  ): JSX.Element {
    const isProcedureDeleted = step.type === 'processprocedure' && step.deleted;
    const linkClasses = cs('text-body d-block title-container', {
      unnamed: isProcedureDeleted,
    });
    return isProcedureDeleted ? (
      <i className={linkClasses}>
        <LazyTooltip
          tooltip={gt(
            'This procedure has been deleted. Remove it from the process and approve a new version.'
          )}
        >
          <span>{content}</span>
        </LazyTooltip>
      </i>
    ) : step.type === 'processprocedure' &&
      !step.deleted &&
      step.title != '' ? (
      <Link to={step.html_url} className={linkClasses}>
        {content}
      </Link>
    ) : step.type === 'processprocedure' &&
      !step.deleted &&
      step.title == '' ? (
      <Link to={step.html_url} className={linkClasses}>
        <i>{content}</i>
      </Link>
    ) : (
      <a href={`#${step.id}`} className={linkClasses}>
        {content}
      </a>
    );
  }

  render() {
    let {
      step,
      collapsed,
      onToggleCollapse,
      mediaURL,
      mediaURLRead,
      actionButtons,
      automation,
      fieldInputs,
      choicesList,
      warnIncomplete,
      orphan,
      next,
      previous,
      showCopyLink,
    } = this.props;
    // ignore steps on process-procedure steps
    let content = step.type !== 'processprocedure' ? step.content || '' : '';
    const images = ('images' in step && step.images) || [];
    const embeds = ('embeds' in step && step.embeds) || [];
    const carousel = [...images, ...embeds];
    const title =
      step.type == 'processprocedure' && step.deleted
        ? gt('Deleted Procedure')
        : step.type == 'processprocedure' && step.title == ''
        ? gt('Untitled Procedure')
        : step.type == 'automation' && step.title == ''
        ? gt('Untitled Procedure')
        : step.title || '';

    return (
      <ProcedureStepStructure
        className={cs({
          'opacity-overlay':
            step.type === 'processprocedure' &&
            !step.deleted &&
            step.approved_at == null,
          'right-to-left-text': isRightToLeftWrapper(title || content || ''),
        })}
        changedState={'changedState' in step ? step.changedState : undefined}
        movedFrom={'movedFrom' in step ? step.movedFrom : undefined}
        warnIncomplete={warnIncomplete}
        n={step.n}
        type={step.type || 'content'}
        beforeStepContent={<Fragment>{previous}</Fragment>}
        beforeTitleContent={
          <Fragment>
            {orphan === true ? <OrphanStepBadge type={step.type} /> : null}
            {step.type === 'processprocedure' && !step.deleted ? (
              <DocIsDraftBadge approved_at={step.approved_at} />
            ) : null}
            <Fragment>
              {actionButtons}
              {step.type !== 'ending' && step.type !== 'processprocedure' ? (
                <StyledCollapseButton
                  className={`${
                    isRightToLeftWrapper(step.title || '')
                      ? 'float-left'
                      : 'float-right'
                  } btn btn-link btn-lg px-1 py-2 d-print-none`}
                  onClick={() => onToggleCollapse(step.id, !collapsed)}
                >
                  <i
                    className={`fa fa-fw fa-chevron-${
                      collapsed ? 'down' : 'up'
                    }`}
                  />
                </StyledCollapseButton>
              ) : null}
            </Fragment>
          </Fragment>
        }
        title={
          <StyledTitleDiv>
            {this.wrapInLinkTag(
              <Fragment>
                {title}
                {showCopyLink === true && !('deleted' in step) ? (
                  <CopyLinkButton
                    className="btn btn-link copy-link ml-1 py-0 px-1"
                    link={`#${step.id}`}
                    tooltip={gt('Copy Step Link')}
                  />
                ) : null}
              </Fragment>,
              step
            )}
          </StyledTitleDiv>
        }
        afterTitleContent={
          <Fragment>
            <div
              className="collapsable-area"
              ref={(el: any) => (this.collapsableEl = el)}
            >
              {content && mediaURL != null && mediaURLRead != null ? (
                <StyledStepContentSeparatorDiv>
                  <UserContentContainer
                    innerRef={(el: any) => (this.collapsableEl = el)}
                    mediaURL={mediaURL}
                    mediaURLRead={mediaURLRead}
                    content={content}
                  />
                </StyledStepContentSeparatorDiv>
              ) : null}

              {carousel.length > 0 ? (
                <div className="images">
                  <ImagesCarousel images={carousel} className="step-images" />
                </div>
              ) : null}

              {fieldInputs}

              {choicesList}

              {automation}

              {'files' in step && step.files.length > 0 ? (
                <ProcedureStepFiles files={step.files} />
              ) : null}
            </div>

            {next}
          </Fragment>
        }
      />
    );
  }
}
